import React from 'react';
import { ActivityIndicator, Modal, StyleSheet, TouchableOpacity, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { Text } from '@/components/ui/text';
import { reviewDesk } from '@/design-system';
import type { ContractWithdrawalFlow } from '../hooks/useContractWithdrawal';
import { COLORS } from '../profileStyles';
import type { Translate } from '../profileTypes';

interface WithdrawContractModalProps {
  withdrawal: ContractWithdrawalFlow;
  t: Translate;
}

/** Confirm step of the EU withdrawal function (BIL-L23). */
export function WithdrawContractModal({ withdrawal, t }: WithdrawContractModalProps) {
  const { showConfirm, loading, isTrial, closeConfirm, confirmWithdrawal } = withdrawal;
  const points = [
    t('profile.withdrawal.pointEndsNow'),
    isTrial ? t('profile.withdrawal.pointTrialNoCharge') : t('profile.withdrawal.pointRefund'),
    t('profile.withdrawal.pointCredits'),
    t('profile.withdrawal.pointEmail'),
  ];

  return (
    <Modal visible={showConfirm} animationType="fade" transparent onRequestClose={closeConfirm}>
      <View style={styles.backdrop}>
        <View style={styles.card}>
          <View style={styles.iconWrap}>
            <MaterialIcons name="assignment-return" size={24} color={COLORS.error} />
          </View>
          <Text style={styles.title}>{t('profile.withdrawal.confirmTitle')}</Text>
          {points.map((point) => (
            <Text key={point} style={styles.copy}>
              {`• ${point}`}
            </Text>
          ))}

          <View style={styles.actions}>
            <TouchableOpacity
              style={[styles.button, styles.cancelButton]}
              disabled={loading}
              onPress={closeConfirm}
              accessibilityRole="button"
            >
              <Text style={styles.cancelText}>{t('profile.withdrawal.keep')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.button, styles.confirmButton, loading && styles.disabledButton]}
              disabled={loading}
              onPress={confirmWithdrawal}
              accessibilityRole="button"
              accessibilityLabel={t('profile.withdrawal.confirm')}
            >
              {loading ? (
                <ActivityIndicator color="#FFFFFF" size="small" />
              ) : (
                <Text style={styles.confirmText}>{t('profile.withdrawal.confirm')}</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
    backgroundColor: 'rgba(17, 24, 39, 0.36)',
  },
  card: {
    width: '100%',
    maxWidth: 460,
    borderRadius: 24,
    borderWidth: 1,
    borderColor: reviewDesk.line,
    backgroundColor: reviewDesk.card,
    padding: 24,
  },
  iconWrap: {
    width: 52,
    height: 52,
    borderRadius: 26,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
    backgroundColor: '#FFF1F2',
  },
  title: {
    color: reviewDesk.ink,
    fontSize: 22,
    fontWeight: '800',
    marginBottom: 12,
  },
  copy: {
    color: reviewDesk.muted,
    fontSize: 15,
    lineHeight: 22,
    marginBottom: 6,
  },
  actions: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 20,
  },
  button: {
    minHeight: 48,
    flex: 1,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
  },
  cancelButton: {
    borderWidth: 1,
    borderColor: reviewDesk.line,
    backgroundColor: reviewDesk.card,
  },
  confirmButton: {
    backgroundColor: COLORS.error,
  },
  disabledButton: {
    opacity: 0.72,
  },
  cancelText: {
    color: reviewDesk.ink,
    fontSize: 15,
    fontWeight: '800',
  },
  confirmText: {
    color: '#FFFFFF',
    fontSize: 15,
    fontWeight: '800',
  },
});
